import type { Address } from 'viem'
import { WETHTransactionItem } from './WETHTransactionTypes'
import {
  TransactionItem,
  type TransactionItemBase,
  type TransactionItemUnion,
  TransactionLabel,
  isNativeAsset,
} from './baseTransactionTypes'
import { EulerSwapTransactionItem } from './eulerSwapTypes'
import type { EvcTransaction } from './evcTransactionTypes'
import { FeeFlowTransactionItem } from './feeFlowTransactionTypes'
import type { PermitTransactionItem } from './permit2TransactionTypes'
import type { RewardStreamsTransactionItem } from './rewardTransactionType'
import { MerklTransactionItem, UnlockREulTransactionItem } from './rewardTransactionType'
import { SwapTransactionItem } from './swapTransactionTypes'
import { SwapVerifierTransaction } from './swapVerifierTransactionTypes'
import { TosTransactionItem } from './tosTransactionTypes'

export type VaultTransactionLabels =
  | TransactionLabel.BORROW
  | TransactionLabel.DEPOSIT
  | TransactionLabel.REPAY
  | TransactionLabel.REPAYWITHSHARES
  | TransactionLabel.WITHDRAW
  | TransactionLabel.REDEEM
  | TransactionLabel.TRANSFER
  | TransactionLabel.ENABLE_COLLATERAL
  | TransactionLabel.DISABLE_COLLATERAL
  | TransactionLabel.ENABLE_BALANCE_FORWARDER
  | TransactionLabel.DISABLE_BALANCE_FORWARDER
  | TransactionLabel.SKIM
  | TransactionLabel.SKIM_IN
  | TransactionLabel.CONVERT_FEES

export type VaultTransaction = {
  label: VaultTransactionLabels
  vault: Address
  transactions?: (
    | VaultTransactionItem
    | SwapTransactionItem
    | RewardStreamsTransactionItem
    | MerklTransactionItem
    | UnlockREulTransactionItem
    | EvcTransaction
    | PermitTransactionItem
    | SwapVerifierTransaction
    | TosTransactionItem
    | FeeFlowTransactionItem
    | SkimTransactionItem
    | WETHTransactionItem
    | EulerSwapTransactionItem
    | TransactionItem
  )[]
}

export type BorrowTransaction = VaultTransaction & {
  label: TransactionLabel.BORROW
}

export type BorrowTransactionItem = TransactionItemBase<BorrowTransaction> & {
  ltv?: number
}

export function isBorrowTransactionItem(item?: TransactionItemUnion): item is BorrowTransactionItem {
  return item?.label === TransactionLabel.BORROW
}

export type DepositTransaction = VaultTransaction & {
  label: TransactionLabel.DEPOSIT
  isWrap?: boolean
  isSavings?: boolean
  // receiver: Address
}

export type DepositTransactionItem = TransactionItemBase<DepositTransaction>

export function isDepositTransactionItem(item?: TransactionItemUnion): item is DepositTransactionItem {
  return item?.label === TransactionLabel.DEPOSIT
}

export type RepayTransaction = VaultTransaction & {
  label: TransactionLabel.REPAY
}

export type RepayTransactionItem = TransactionItemBase<RepayTransaction>

export function isRepayTransactionItem(item?: TransactionItemUnion): item is RepayTransactionItem {
  return item?.label === TransactionLabel.REPAY
}

export type RepayWithSharesTransaction = VaultTransaction & {
  label: TransactionLabel.REPAYWITHSHARES
}

export type RepayWithSharesTransactionItem = TransactionItemBase<RepayWithSharesTransaction>

export function isRepayWithSharesTransactionItem(item?: TransactionItemUnion): item is RepayWithSharesTransactionItem {
  return item?.label === TransactionLabel.REPAYWITHSHARES
}

export type WithdrawTransaction = VaultTransaction & {
  label: TransactionLabel.WITHDRAW
  onBehalfOf?: Address
  isUnwrap?: boolean
  userShares?: string
  isSavings?: boolean
}

export type WithdrawTransactionItem = TransactionItemBase<WithdrawTransaction>

export function isWithdrawTransactionItem(item?: TransactionItemUnion): item is WithdrawTransactionItem {
  return item?.label === TransactionLabel.WITHDRAW
}

export type SkimTransaction = VaultTransaction & {
  label: TransactionLabel.SKIM
  onBehalfOf?: Address
}

export type SkimTransactionItem = TransactionItemBase<SkimTransaction>

export function isSkimTransactionItem(item?: TransactionItemUnion): item is SkimTransactionItem {
  return item?.label === TransactionLabel.SKIM
}

/**
 * SkimIn: Push tokens from user wallet to vault, then skim into shares.
 *
 * Generates two EVC calls:
 * 1. Permit2.transferFrom(owner, vault, amount, token) - push tokens to vault
 * 2. vault.skim(amount, receiver) - convert tokens to shares
 *
 * Used for multiply flow where we need to deposit collateral but can't use
 * deposit() because account already has a controller enabled.
 */
export type SkimInTransaction = VaultTransaction & {
  label: TransactionLabel.SKIM_IN
  /** The owner address that tokens will be transferred from (usually main wallet) */
  owner: Address
}

export type SkimInTransactionItem = TransactionItemBase<SkimInTransaction>

export function isSkimInTransactionItem(item?: TransactionItemUnion): item is SkimInTransactionItem {
  return item?.label === TransactionLabel.SKIM_IN
}

export type TransferTransaction = VaultTransaction & {
  label: TransactionLabel.TRANSFER
  sharesValue: string
}

export type TransferTransactionItem = TransactionItemBase<TransferTransaction>

export function isTransferTransactionItem(item?: TransactionItemUnion): item is TransferTransactionItem {
  return item?.label === TransactionLabel.TRANSFER
}

export type ToggleCollateralTransaction = VaultTransaction & {
  label: TransactionLabel.ENABLE_COLLATERAL | TransactionLabel.DISABLE_COLLATERAL
}

export type ToggleCollateralTransactionItem = TransactionItemBase<ToggleCollateralTransaction>

export function isToggleCollateralTransactionItem(
  item?: TransactionItemUnion,
): item is ToggleCollateralTransactionItem {
  return item?.label === TransactionLabel.ENABLE_COLLATERAL || item?.label === TransactionLabel.DISABLE_COLLATERAL
}

export type RedeemTransaction = VaultTransaction & {
  label: TransactionLabel.REDEEM
}

export type RedeemTransactionItem = TransactionItemBase<RedeemTransaction>

export function isRedeemTransactionItem(item?: TransactionItemUnion): item is RedeemTransactionItem {
  return item?.label === TransactionLabel.REDEEM
}

export type EnableBalanceForwarderTransaction = VaultTransaction & {
  label: TransactionLabel.ENABLE_BALANCE_FORWARDER
}

export type EnableBalanceForwarderTransactionItem = TransactionItemBase<EnableBalanceForwarderTransaction>

export function isEnableBalanceForwarderTransactionItem(
  item?: TransactionItemUnion,
): item is EnableBalanceForwarderTransactionItem {
  return item?.label === TransactionLabel.ENABLE_BALANCE_FORWARDER
}

export type DisableBalanceForwarderTransaction = VaultTransaction & {
  label: TransactionLabel.DISABLE_BALANCE_FORWARDER
}

export type DisableBalanceForwarderTransactionItem = TransactionItemBase<DisableBalanceForwarderTransaction>

export function isDisableBalanceForwarderTransactionItem(
  item?: TransactionItemUnion,
): item is DisableBalanceForwarderTransactionItem {
  return item?.label === TransactionLabel.DISABLE_BALANCE_FORWARDER
}

export type TxOperatingFromMainAccount = DepositTransactionItem | RepayTransactionItem | SkimInTransactionItem

export function isTxOperatingFromMainAccount(tx: TransactionItemUnion): tx is TxOperatingFromMainAccount {
  return isDepositTransactionItem(tx) || isRepayTransactionItem(tx) || isSkimInTransactionItem(tx)
}

export type TxThatNeedsApproval = DepositTransactionItem | RepayTransactionItem | SkimInTransactionItem

export function isTxThatNeedsApproval(tx: TransactionItemUnion): tx is TxThatNeedsApproval {
  return (
    ((isDepositTransactionItem(tx) && !tx.isWrap) || isRepayTransactionItem(tx) || isSkimInTransactionItem(tx)) &&
    !isNativeAsset(tx.asset)
  )
}

export type TsxAmountIn =
  | DepositTransactionItem
  | RepayTransactionItem
  | TransferTransactionItem
  | SkimInTransactionItem

export function isTsxAmountIn(tx: TransactionItemUnion): tx is TsxAmountIn {
  return isDepositTransactionItem(tx) || isRepayTransactionItem(tx) || isTransferTransactionItem(tx)
}

export type ConvertFeesTransaction = VaultTransaction & {
  label: TransactionLabel.CONVERT_FEES
}

export type ConvertFeesTransactionItem = TransactionItemBase<ConvertFeesTransaction>

export function isConvertFeesTransactionItem(item?: TransactionItemUnion): item is ConvertFeesTransactionItem {
  return item?.label === TransactionLabel.CONVERT_FEES
}

export type VaultTransactionItem = TransactionItemBase<VaultTransaction>

export function isWrappingOrUnwrapping(item?: TransactionItemUnion) {
  return (isDepositTransactionItem(item) && item.isWrap) || (isWithdrawTransactionItem(item) && item.isUnwrap)
}
